/* eslint-disable import/prefer-default-export */
/* eslint-disable no-console */
/* eslint-disable camelcase */
import axios from 'axios';
import catchify from 'catchify';
import get from 'lodash/get';
import set from 'lodash/set';

import {
  GET_PLANS_ERROR_MESSAGE,
  GET_CURRENT_PLAN_ERROR_MESSAGE,
  GET_USER_CARDS_ERROR_MESSAGE,
  PAYMENT_METHODS,
  STEPS,
  getStripeScriptUrl,
  getPayPalScriptUrl,
  SAVE_NEW_CARD_ERROR_MESSAGE,
  REDUCE_TIER_REQUEST,
  ENHANCE_TIER_REQUEST,
  CONFIRM_CHANGE_ERROR,
  STRIPE_ELEMENT_SLUG,
  STRIPE_ELEMENTS_PAYMENT_OPTIONS,
} from '@/constants/change-plan.js';
import { getCSRFCookie } from '~/util/global';

const buildApiRequest = (axiosInstance = axios, url, method = 'get', data = {}) => (
  catchify(axiosInstance({
    data,
    url,
    method,
    withCredentials: true,
    headers: {
      'X-CSRFToken': getCSRFCookie(),
    },
  }))
);

const calculateSubtotal = (currentPlan, newPlan, discountRate = 0) => {
  const subtotal = Number(
    Number(newPlan.price * (1 - discountRate)).toFixed(2)
    - Number(currentPlan.previous_balance_applied),
  ).toFixed(2);
  if (currentPlan.tier < newPlan.tier) {
    // If user is upgrading, we don't want a negative number
    return Math.max(
      0,
      subtotal,
    );
  }
  return subtotal;
};

// Step validator functions
const validateBillingStep = (getters) => getters.isBillingDetailsValid && getters.isABNValid;
const validateSelectTierStep = (getters) => !!getters.newPlanSelected;
const validatePaymentStep = (getters, rootGetters) => {
  // Credit card method selected, we need to make sure they actually select a card to pay with.
  if (getters.selectedPaymentMethod.includes('credit-card')) {
    return !!getters.selectedPaymentMethodObject;
  }

  if (getters.selectedPaymentMethod === 'stripe-elements') {
    return rootGetters['stripeElements/isComplete'];
  }

  // For paypal and credits, we don't need any fancy validation (yet).
  return getters.selectedPaymentMethod === 'paypal' || getters.selectedPaymentMethod === 'credits';
};
const validateReviewStep = validatePaymentStep;
const validators = [
  validateSelectTierStep,
  validateBillingStep,
  validatePaymentStep,
  validateReviewStep,
];

const initialState = () => ({
  errorMessage: null,
  isProcessing: false,
  isBillingDetailsValid: false,
  isABNValid: false,
  // This is set to true until the change plan request
  // has been actually completed (success or error)
  // which means that the user has clicked the "Confirm" button in the last step.
  // Also set to true when the PayPal payment fails.
  hasCompletedChangePlanRequest: false,
  // The string key of the current step in the flow.
  currentStepKey: 'select-tier',
  // List of all plans that are shown to users, including the one that user currently has
  availablePlans: [],
  // Plan that user currently has (in the DB)
  currentPlan: null,
  // Plan that user selected on the UI (this is always different than the currentPlan)
  newPlanSelected: null,
  // Whether or not user is downgrading his/her current plan
  isRefunding: false,
  // Credit/Debit cards that user has in stripe/DB.
  userCards: [],
  // Payment method selected by the user in the UI.
  // This can be credit-card-new, credit-card-${id} (stripe card id), paypal, or credits.
  selectedPaymentMethod: PAYMENT_METHODS[0].key,
  // This object represents the actual selected payment method.
  // If selectedPaymentMethod is credit-card-new, then this object is null until they add a card.
  // If selectedPaymentMethod is credit-card-${id}, then this is the stripe credit card object.
  // If selectedPaymentMethod is paypal, then this object is null.
  // If selectedPaymentMethod is credits, then this object is null.
  selectedPaymentMethodObject: null,
  // This represents the PayPal order object
  // and is changed after the user completes paying with PayPal.
  paypalOrderObject: null,
});

export const mutations = {
  setErrorMessage(state, value) {
    set(state, 'errorMessage', value);
  },

  setIsProcessing(state, value) {
    set(state, 'isProcessing', value);
  },

  setHasCompletedChangePlanRequest(state, value) {
    set(state, 'hasCompletedChangePlanRequest', value);
  },

  setAvailablePlans(state, value) {
    set(state, 'availablePlans', value);
  },

  setCurrentPlan(state, value) {
    set(state, 'currentPlan', value);
  },

  setNewPlanSelected(state, value) {
    set(state, 'newPlanSelected', value);
  },

  setIsRefunding(state, value) {
    set(state, 'isRefunding', value);
  },

  setCurrentStepKey(state, value) {
    set(state, 'currentStepKey', value);
  },

  setIsBillingDetailsValid(state, value) {
    set(state, 'isBillingDetailsValid', value);
  },

  setIsABNValid(state, value) {
    set(state, 'isABNValid', value);
  },

  setUserCards(state, value) {
    set(state, 'userCards', value);
  },

  setSelectedPaymentMethod(state, value) {
    set(state, 'selectedPaymentMethod', value);

    if (value === STRIPE_ELEMENT_SLUG) {
      const pmObj = {
        key: 'stripe-elements-payment',
        id: STRIPE_ELEMENT_SLUG,
        brand: 'card',
        paymentType: STRIPE_ELEMENT_SLUG,
        label: STRIPE_ELEMENTS_PAYMENT_OPTIONS?.card?.label,
      };

      set(state, 'selectedPaymentMethodObject', pmObj);
    } else if (!value.includes('credit-card')) {
      // Update selectedPaymentMethodObject based on selected payment method
      set(state, 'selectedPaymentMethodObject', null);
    } else {
      const cardId = value.split('credit-card-')[1];
      const selectedCard = state.userCards.find((card) => card.id === cardId);
      set(state, 'selectedPaymentMethodObject', selectedCard);
    }
  },

  setSelectedPaymentMethodObject(state, value) {
    set(state, 'selectedPaymentMethodObject', value);
  },

  setPaypalOrderObject(state, value) {
    set(state, 'paypalOrderObject', value);
  },

  setPaypalOrderAuthorizationId(state, value) {
    set(state, 'paypalOrderAuthorizationId', value);
  },
};

export const getters = {
  errorMessage: (state) => state.errorMessage,

  isABNValid: (state) => state.isABNValid,

  isBillingDetailsValid: (state) => state.isBillingDetailsValid,

  isProcessing: (state) => state.isProcessing,

  hasCompletedChangePlanRequest: (state) => state.hasCompletedChangePlanRequest,

  availablePlans: (state) => state.availablePlans,

  currentPlan: (state) => state.currentPlan,

  newPlanSelected: (state) => state.newPlanSelected,

  isRefunding: (state) => state.isRefunding,

  userCards: (state) => state.userCards,

  selectedPaymentMethod: (state) => state.selectedPaymentMethod,

  selectedPaymentMethodObject: (state) => state.selectedPaymentMethodObject,

  currentStepKey: (state) => state.currentStepKey,

  steps: (state) => {
    let steps = [...STEPS];

    // If user is downgrading the plan then we won't show the payment step
    // since the refund will go directly to their default payment type.
    if (state.isRefunding) {
      steps = steps.filter((step) => step.key !== 'select-payment' && step.key !== 'billing-details');
    }

    return steps;
  },

  validatedSteps: (state, stateGetters, rootState, rootGetters) => {
    const initialSteps = [...stateGetters.steps];
    let isPreviousStep = true;
    let previousStepIsValid = true;

    const filteredValidators = [...validators];

    return initialSteps.map((step, index) => {
      const isReviewStep = step.key === 'review';
      let isDisabled = true;
      let isValid = false;
      let isCurrentStep = false;

      // Only validate if previous step is valid
      // Example: If step 2 is invalid, all subsequent steps are disabled in the stepper
      if (previousStepIsValid) {
        // We don't want/need to validate the review step
        isValid = isReviewStep ? false : filteredValidators[index](stateGetters, rootGetters);

        isCurrentStep = step.key === stateGetters.currentStepKey;
        if (isCurrentStep) {
          isPreviousStep = false;
        }

        // Enable step if:
        // - is valid
        // - is previous step
        // - is current step
        // - previous step is valid
        if (isValid || isPreviousStep || isCurrentStep || previousStepIsValid) {
          isDisabled = false;
        }

        previousStepIsValid = isValid;
      }

      isDisabled = isReviewStep ? stateGetters.currentStepKey !== 'review' : isDisabled;

      return {
        ...step,
        number: (index + 1),
        valid: isValid,
        disabled: isDisabled,
        className: isDisabled && 'disabled',
        isPreviousStep,
        isCurrentStep,
      };
    });
  },

  memberDiscount(state, stateGetters, rootState, rootGetters) {
    const hasPlus = get(rootGetters, 'userSession.has_plus', false);
    const hasBriefcase = get(rootGetters, 'userSession.has_briefcase', false);

    if (hasPlus || hasBriefcase) {
      const { currentPlan, newPlanSelected } = stateGetters;
      const newDiscountRate = get(currentPlan, 'current_plan_discount_percentage', 0) / 100;
      const discountedPrice = Number(
        calculateSubtotal(currentPlan, newPlanSelected, newDiscountRate),
      ).toFixed(2);
      const regularPrice = Number(calculateSubtotal(currentPlan, newPlanSelected)).toFixed(2);
      return regularPrice - discountedPrice;
    }

    return 0;
  },

  paymentSubTotal: (state, stateGetters) => {
    const { currentPlan, newPlanSelected } = stateGetters;
    return calculateSubtotal(currentPlan, newPlanSelected);
  },

  paymentTotal(state, stateGetters) {
    if (stateGetters.isRefunding) {
      return Object
        .values(stateGetters.reductionDetails?.amounts)
        .reduce((a, b) => a + b);
    }

    return Math.abs(parseFloat((
      Number(stateGetters.paymentSubTotal)
        + stateGetters.paymentTaxes
        - stateGetters.memberDiscount
    )));
  },

  paymentTaxes(state, stateGetters, rootState, rootGetters) {
    const taxRate = rootGetters['stripeElements/taxRate'];

    if (stateGetters.isRefunding) {
      return Number(stateGetters.reductionDetails?.tax) || 0;
    }

    if (!taxRate) {
      return 0;
    }

    return Math.abs(
      parseFloat(taxRate * (stateGetters.paymentSubTotal - stateGetters.memberDiscount)) / 100,
    );
  },

  reductionDetails(state, stateGetters) {
    const { currentPlan, newPlanSelected } = stateGetters;

    return currentPlan.refund_amounts_per_plan
      .find((item) => item.plan_tier === newPlanSelected.tier);
  },

  plusPlanPrice: (state, stateGetters, rootState, rootGetters) => {
    const hasPlus = get(rootGetters, 'userSession.has_plus', false);
    if (hasPlus) {
      const { currentPlan, newPlanSelected } = stateGetters;
      const newDiscountRate = get(currentPlan, 'current_plan_discount_percentage', 0) / 100;
      return calculateSubtotal(currentPlan, newPlanSelected, newDiscountRate);
    }
    return stateGetters.paymentSubTotal;
  },
};

export const actions = {
  async getAvailablePlans({ commit }, { dealId }) {
    const availablePlansUrl = '/api/dealplans/?format=json'
      + '&filter{is_active}=true&include[]=plan_features'
      + '&include[]=deal.listing_type&include[]=deal.deal_status'
      + `&include[]=deal.taxonomy&filter{deal.id}=${dealId}`;

    const [error, response] = await buildApiRequest(this.$axios, availablePlansUrl);
    if (error) {
      console.error('Error fetching available plans: ', error);
      commit('setErrorMessage', GET_PLANS_ERROR_MESSAGE);
      return;
    }

    const { data: { plans } } = response;
    // Transform feature configs into user readable strings.
    plans.forEach((plan) => {
      const { plan_features: features } = plan;
      features.forEach((feature) => {
        if (feature.code_to_feature_conf) {
          const entry = feature.code_to_feature_conf.find((c) => c.codes === plan.tier);

          if (entry) {
            // eslint-disable-next-line no-param-reassign
            feature.feature = feature.feature.replace('{{}}', `<strong>${entry.quantity}</strong>`);
          }
        }
      });

      // eslint-disable-next-line no-param-reassign
      plan.plan_features = plan.plan_features.slice(0, 6);
    });

    commit('setAvailablePlans', plans);
  },

  async getCurrentPlan({ commit }, uuid) {
    const currentPlanUrl = '/api/userinvoices/?format=json&include[]=deal_plan&include[]=deal&exclude[]=invoice.*'
      + '&include[]=invoice.payment_type&include[]=invoice.enhanced_invoice_items'
      + `&include[]=invoice.total&filter{uuid}=${uuid}`;

    const [error, response] = await buildApiRequest(this.$axios, currentPlanUrl);
    if (error) {
      console.error('Error fetching current plan: ', error);
      commit('setErrorMessage', GET_CURRENT_PLAN_ERROR_MESSAGE);
      return;
    }

    const { data: { invoiceitems } } = response;
    const [item] = invoiceitems;
    const {
      deal,
      invoice,
      original_invoice_payment_type,
      refund_info,
      lowest_available_plan_tier,
      gifted_plan_tier,
      refund_amounts_per_plan,
      can_enhance_license,
      deal_plan: plan,
      current_plan_discount_percentage,
    } = item;

    commit('setCurrentPlan', {
      ...plan,
      deal,
      invoice,
      refund_info,
      original_invoice_payment_type,
      lowest_available_plan_tier,
      gifted_plan_tier,
      refund_amounts_per_plan,
      can_enhance_license,
      uuid: item.uuid,
      license_key: item.license_key,
      total_without_tax: item.total_without_tax,
      previous_balance_applied: parseFloat(item.previous_balance_applied || 0),
      current_plan_discount_percentage,
    });
  },

  async fetchUserCards({ commit, state }) {
    // Credit cards already fetched
    if (
      state.userCards?.length
      && state.selectedPaymentMethod
      && state.selectedPaymentMethodObject
    ) {
      return;
    }

    const [error, response] = await buildApiRequest(this.$axios, '/api/cart/stripecards/?format=json');

    if (error) {
      console.error('Error fetching user cards: ', error);
      commit('setErrorMessage', GET_USER_CARDS_ERROR_MESSAGE);
      return;
    }

    const { data } = response;
    commit('setUserCards', data);

    // We need to set the default credit card as selected payment method.
    if (data && data.length && !state.selectedPaymentMethodObject) {
      let defaultCard = data.find((card) => card.is_default);

      if (!defaultCard) {
        [defaultCard] = data;
      }

      if (defaultCard) {
        commit('setSelectedPaymentMethod', `credit-card-${defaultCard.id}`);
        commit('setSelectedPaymentMethodObject', defaultCard);
      }
    } else {
      commit('setSelectedPaymentMethod', '');
      commit('setSelectedPaymentMethodObject', null);
    }
  },

  async saveNewCard({ commit, state }, payload) {
    const {
      stripeLib,
      stripeCard,
      holderName,
      billingAddress,
      city,
      stateOrProvince,
      country,
    } = payload;

    commit('setErrorMessage', null);
    commit('setIsProcessing', true);

    let [error, response] = await catchify(stripeLib.createToken(stripeCard, {
      name: holderName,
      address_line1: billingAddress,
      address_city: city,
      address_state: stateOrProvince,
      address_country: country,
    }));

    if (error) {
      console.error('Error saving new card: ', error);
      commit('setIsProcessing', false);
      commit('setErrorMessage', SAVE_NEW_CARD_ERROR_MESSAGE);
      return;
    }

    const newCardData = { stripeToken: response.token.id };
    [error, response] = await buildApiRequest(
      this.$axios,
      '/api/cart/stripecards/',
      'post',
      newCardData,
    );

    commit('setIsProcessing', false);

    if (error) {
      console.error('Error saving new card: ', error);
      commit('setErrorMessage', SAVE_NEW_CARD_ERROR_MESSAGE);
      return;
    }

    commit('setErrorMessage', null);

    // Update user cards, push the new one.
    // Also set the new card as the selected payment method.
    const newCard = response.data;
    const newUserCards = [...state.userCards];
    newUserCards.push(newCard);
    commit('setUserCards', newUserCards);
    commit('setSelectedPaymentMethod', `credit-card-${newCard.id}`);
    commit('setSelectedPaymentMethodObject', newCard);
  },

  async updatePlan({ commit, state, rootGetters }) {
    // Returns a redirect URL if one is required.
    const { isRefunding } = state;
    const postData = {
      action: isRefunding ? REDUCE_TIER_REQUEST : ENHANCE_TIER_REQUEST,
      plan: state.newPlanSelected.id,
    };

    if (isRefunding) {
      postData.refund_type = 'default';
    } else {
      const { selectedPaymentMethod } = state;
      if (selectedPaymentMethod === 'credits') {
        postData.payment_method = 'credit';
      } else if (selectedPaymentMethod === 'paypal') {
        postData.payment_method = 'paypal';
        postData.paypal_order_id = state.paypalOrderObject.orderID;
        postData.paypal_authorization_id = state.paypalOrderAuthorizationId;
      } else {
        postData.payment_method = 'stripe';
        postData.stripe_token = state.selectedPaymentMethodObject.id;
      }

      postData.billing_country = rootGetters['stripeElements/billingCountry'];
      postData.billing_postal_code = rootGetters['stripeElements/billingPostalCode'];
      postData.billing_tax_id = rootGetters['stripeElements/billingTaxId'];
    }

    commit('setErrorMessage', null);
    commit('setIsProcessing', true);
    // debugger

    let url = `/license/update/${state.currentPlan.license_key}/`;
    if (state.currentPlan.deal.is_licensing_v2) {
      url = `/licensing/v2/update/${state.currentPlan.uuid}/`;
      postData.deal_plan = state.newPlanSelected.id;
    }

    const [error, response] = await buildApiRequest(
      this.$axios,
      url,
      'put',
      postData,
    );

    if (get(response, 'data.client_secret', null)) {
      return get(response, 'data');
    }

    if (get(response, 'data.redirection_url', null)) {
      return get(response, 'data');
    }

    commit('setIsProcessing', false);
    commit('setHasCompletedChangePlanRequest', true);

    if (error) {
      const errorMessage = get(error, 'response.data.message', CONFIRM_CHANGE_ERROR);
      console.error(`Error in ${postData.action} request: `, error);
      commit('setErrorMessage', errorMessage);
    }
    return null;
  },

  loadPaymentGatewayLibraries(_context, { vueInstance, paypalKey }) {
    vueInstance.$loadScript(getStripeScriptUrl());
    vueInstance.$loadScript(getPayPalScriptUrl(paypalKey));
  },
};

export { initialState as state };
